/**
 * What the page becomes on paper (ROADMAP "Share, not export…" item 3).
 *
 * The product offers Print rather than a file download, so this IS the
 * document: without it a browser print of the app produces navigation, buttons
 * and a page of ink.
 *
 * **It lives here, as a mounted `GlobalStyles`, and not in `src/index.css`.**
 * That file — and `src/App.css` beside it — are imported by nothing; the app
 * loads its styling through MUI's theme. A print stylesheet put there would
 * have looked right in the diff, passed a test that read the file, and done
 * absolutely nothing in a browser. It did, until a real print showed the
 * navigation still on the page.
 *
 * Every rule answers a specific, currently-unfixed browser behaviour rather
 * than a preference:
 *
 *   `print-color-adjust: exact` — browsers strip backgrounds to save ink. This
 *     is the standardised property; `color-adjust` is the deprecated spelling.
 *   `display: block` on flex/grid — Chrome and Safari suppress page breaks
 *     INSIDE a flex container, so a long column prints as one unbreakable slab.
 *   `background-color: white` on canvas — a canvas bitmap is TRANSPARENT, and
 *     `print-color-adjust` does not reach inside it. Charts render to canvas
 *     here, so without a surface behind them the series print onto nothing.
 *   `break-inside: avoid` — a card or table split across a page boundary is
 *     what makes a printed report unreadable.
 */
export const PRINT_STYLES = `
@media print {
  *, *::before, *::after {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  html, body {
    background: white !important;
    color: black !important;
    margin: 0;
    padding: 0;
  }

  /* The application, as opposed to the document. */
  nav,
  header,
  footer,
  .MuiAppBar-root,
  .MuiDrawer-root,
  .MuiSpeedDial-root,
  .MuiTooltip-popper,
  .MuiSnackbar-root,
  .MuiFab-root,
  [role='navigation'],
  [role='dialog'],
  .no-print {
    display: none !important;
  }

  /* Breaks land between things, not through them. */
  /* Leaf units only. The Paper root selector is deliberately absent: the page's own
     container is a Paper too, and marking it unbreakable pushed the whole
     report onto page 2 and left page 1 empty — measured, not theorised. */
  .MuiCard-root,
  .MuiTable-root,
  figure,
  table {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  /* Chrome and Safari do not fragment a flex container across pages, and the
     documented workaround is to make it a block for print. That is DELIBERATELY
     NOT done here: measured on a real print, flattening every Grid container
     turned each row of metrics into a vertical stack and tripled the document's
     length. The harm from the workaround is certain and on every page; the harm
     it prevents is conditional on a container being taller than a page. Keeping
     these whole is the better trade, and a specific tall container that
     paginates badly is a targeted fix, not a blanket one. */
  .MuiGrid-container {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  /* A transparent canvas prints as nothing. Give every chart a surface. */
  canvas, svg {
    background-color: white !important;
    max-width: 100% !important;
    break-inside: avoid;
    page-break-inside: avoid;
  }

  /* Belt and braces on chart width, and stated as such rather than as a fix for
     something observed. Measured 2026-09-08 on a real print of the research
     page: the plot surface DOES re-measure when the page is laid out at paper
     width (1094px on screen -> 664px on A4) and prints complete — the printed
     chart carries fewer date labels only because the library thins its own
     ticks as it narrows. So this rule changed nothing on that page. It is kept
     because a surface that does NOT re-measure in time has no other bound: it
     would keep its screen width and simply be cut off by the page edge, with
     nothing printed to say anything was missing. These surfaces carry a
     viewBox, so a relative width scales the plot instead of cropping it. */
  .recharts-wrapper,
  .recharts-surface,
  .ag-chart-wrapper,
  .ag-charts-focus-indicator {
    width: 100% !important;
    max-width: 100% !important;
  }
  .recharts-surface {
    height: auto !important;
  }

  /* There is no scrolling on paper — but this is deliberately NOT *. A
     blanket overflow: visible also un-clips every chart, and the price trend
     then draws straight through the right-hand edge of its own card (measured).
     Only the containers that genuinely hide content by scrolling are opened. */
  .MuiTableContainer-root,
  pre,
  code {
    overflow: visible !important;
    max-height: none !important;
  }
}
`;
